/**
 * HelpButton — per-page contextual "?" icon that pops a side-sheet with
 * the relevant help topic. Drop it into any PageHeader's `action` slot:
 *
 *   <PageHeader ... action={<HelpButton topic="temperature" />} />
 *
 * The sheet renders the SAME content as /help/<topic> so we keep one
 * source of truth in lib/helpContent.js.
 */
import { Link } from "react-router-dom";
import { HelpCircle, ExternalLink } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { getHelpTopic } from "@/lib/helpContent";
import { HelpBody } from "@/pages/HelpTopic";

export default function HelpButton({ topic, label = "Help" }) {
  const t = getHelpTopic(topic);
  if (!t) return null;
  const Icon = t.icon;
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          data-testid={`help-btn-${topic}`}
          variant="ghost"
          size="icon"
          className="h-10 w-10 text-eucalyptus-900 hover:bg-eucalyptus-100"
          aria-label={label}
        >
          <HelpCircle className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid={`help-sheet-${topic}`}>
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-eucalyptus-100 text-eucalyptus-900 grid place-items-center">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <SheetTitle className="font-display text-left">{t.title}</SheetTitle>
              <SheetDescription className="text-left text-xs">{t.blurb}</SheetDescription>
            </div>
          </div>
        </SheetHeader>
        <div className="mt-6">
          <HelpBody body={t.body} />
        </div>
        <div className="mt-8 pt-4 border-t border-[color:var(--border)]">
          <Link
            to={`/help/${t.slug}`}
            data-testid={`help-sheet-open-full-${topic}`}
            className="inline-flex items-center gap-1.5 text-sm text-eucalyptus-900 underline-offset-4 hover:underline"
          >
            Open full help page <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
